import React from 'react';
import _has from 'lodash/has';
import { GraphType } from '../../../../../../../../slide/infrastructure/module/dependent/shape-info/constant';
import _get from 'lodash/get';
import Cursor from '@src/css/ppt-editor-cursor-global.less';
import connect from '@src/js/separate-logic/connect';
import GraphicContentModel from './graphic-content-model';
import GraphicContentPresenter from './graphic-content-presenter';
import { ImagePropsField } from '@src/slide/domain/ui-model/element-modeles/property/image-operation/constant/props-value';
import { IBaseGraphics } from '@tencent/alloy-graphics';
import type StrokeInfo from '@src/framework/editor/src/page/index/components/canvas/graphic-content/bg-shapes/stroke-info';
import type FillBackground from '@src/framework/editor/src/page/index/components/canvas/fill-background';

const FILL_RECT = 'fillRect';
const SRC_RECT = 'srcRect';
const FILL_AND_STROKE = 'fill_stroke';  // 填充和边框

// 1.声明props类型
interface IGraphicContentProps{
    // 父组件props
    model: any;
    asSvgContent: boolean;
    className: string;
    style: any;
    fillMode: number;
    cursor: string;
    editable: boolean;
    pointerEvents: string;
    imageBorderOutside: boolean;
    relativeRotate: number;
    relativeFlipH: number;
    relativeFlipV: number;
    playGif: boolean;
    original: boolean;
    scale: number;

    // presenter props
    setRef: (name: string, ref: any) => void;

    // model props
    pathParser: IBaseGraphics;
    strokeInfo: StrokeInfo;
    fillBackground: FillBackground;
    effectFilter: any;
    imageFilter: any;
    isFillRect?: string;    // 是否是填充rect
}

/**
 * 核心图形渲染组件
 */
class GraphicContent extends React.Component<IGraphicContentProps> {
    public static displayName = 'GraphicContent'; // 声明displayName，单元测试用于查找组件

    // 2.设定默认值，防止出错
    public static defaultProps = {
        setRef: () => {},
    };

    public render() {
        const { setRef, model, pathParser, asSvgContent, className, style, imageFilter } = this.props;

        const isCustom = model.bgShape === GraphType.CUSTOM;
        const { left, top, width, height, image } = model;
        let paths;
        if (!isCustom) {
            paths = pathParser.svgPathInRect();
        } else {
            paths = model.pathList;
        }
        // SVG大小取整，否则图片会变模糊 add by yunnan
        const intWidth = Math.round(width);
        const intHeight = Math.round(height);
        const filter = imageFilter?.getBrightnessSaturateFilter(image);

        return (
            <React.Fragment>
                {
                    asSvgContent
                        ? <g ref={(ref) => {
                            setRef('domRef', ref);
                        }}
                        transform={`translate(${left} ${top})`}>
                            { paths?.length > 0 && paths.map(this.renderPath) }
                        </g>
                        : <React.Fragment>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                version="1.1"
                                ref={(ref) => {
                                    setRef('domRef', ref);
                                }}
                                className={className}
                                style={{
                                    left: 0,  // 不能去掉，父组件可能传递position属性
                                    top: 0,
                                    width: width > 1 ? intWidth : 1,
                                    height: height > 1 ? intHeight : 1,
                                    // position: 'absolute', // 减少position，减少回流时间
                                    overflow: 'visible',
                                    filter,
                                    ...style,
                                }}>
                                { paths?.length > 0 && paths.map(this.renderPath) }
                            </svg>
                        </React.Fragment>
                }
            </React.Fragment>
        );
    }

    private getRelativeFlip(model) {
        let relativeFlipH = this.props.relativeFlipH || 0;
        let relativeFlipV = this.props.relativeFlipV || 0;
        if (model.flipH === 1) {
            relativeFlipH = (relativeFlipH === 0 ? 1 : 0);
        }
        if (model.flipV === 1) {
            relativeFlipV = (relativeFlipV === 0 ? 1 : 0);
        }
        return { relativeFlipH, relativeFlipV };
    }

    private renderBg = (fillMode, path, pointerEvents, cursor, transformStr?, scaleW?, scaleH?, imageFiltersId?) => {
        const { model, fillBackground, relativeRotate, playGif, original } = this.props;

        let rotate = (model.rotate || 0) + (relativeRotate || 0);
        rotate = rotate >= 360 ? rotate - 360 : rotate;

        const { relativeFlipH, relativeFlipV } = this.getRelativeFlip(model);
        const bgOption = {
            path,
            cursor,
            transformStr,
            rotate: rotate || 0,
            flipV: relativeFlipV,
            flipH: relativeFlipH,
            scaleW,
            scaleH,
            pointerEvents,
            playGif,
            original,
            imageFiltersId,
        };
        const { fillBackground: fillData, width, height } = model;

        const srcRect = model?.rect?.srcRect;
        if (this.hasImageBg() && srcRect) {
            const rectKey = model.isFillRect ? FILL_RECT : SRC_RECT;
            if (fillData.image.stretch) {
                fillData.image.stretch[rectKey] = srcRect;
            } else {
                fillData.image.stretch = { [rectKey]: { ...srcRect }  };
            }
        }
        return fillBackground.renderBg(fillMode, fillData, width, height, bgOption);
    };

    private hasImageBg() {
        return this.props.model.fillBackground?.image;
    }

    private getScaleNum() {
        return _has(this, 'props.scale') ? this.props.scale : 1;
    }

    /**
     * 获取背景填充样式
     * @param fillMode
     */
    private getFillStyle = (fillMode?) => {
        const { model, fillBackground } = this.props;
        const fillData = model.fillBackground;
        return fillBackground.getFillStyle(fillData, fillMode);
    };

    /**
     * 获取背景透明度
     */
    private getFillOpacity = () => this.props.fillBackground.getOpacity(); // 在getFillStyle后调用

    private getScaleInfo(path, model) {
        let pathWidth = _get(path, 'width') || model.width * 12700;
        let pathHeight = _get(path, 'height') || model.height * 12700;
        if (pathWidth === 0) pathWidth = 1;
        if (pathHeight === 0) pathHeight = 1;
        let scaleW = model.width / pathWidth;
        let scaleH = model.height / pathHeight;
        if (scaleW === 0) scaleW = 1 / 12700;
        if (scaleH === 0) scaleH = 1 / 12700;
        const tranStr = `scale(${scaleW} ${scaleH})`;
        return { scaleW, scaleH, tranStr };
    }

    private getStrokeOpacity(path, strokeData, isCustom) {
        const opacity = (strokeData.strokeWidth > 0 && strokeData.strokeColor)
            ? strokeData.opacity : 0;
        if (isCustom) {
            return !path?.notStroke ? opacity : null;
        }
        return !path?.notStroke ? opacity : 0;
    }


    private getFillStyleByPath(path, isCustom) {
        const fillMode = this.getFillMode(path, isCustom);
        const customFillMode = isCustom ? _get(path, 'fill') : '';
        return customFillMode === 'none' ? 'none' : this.getFillStyle(fillMode);
    }

    private getFillMode(path, isCustom) {
        let fillMode;
        if (!isCustom) {
            fillMode = this.props.fillMode ? this.props.fillMode : _get(path, 'fillMode');
        }
        return fillMode;
    }

    /**
     * 获取path的render相关数据
     * @param path
     */
    private getPathRenderInfo(path) {
        const { imageBorderOutside, model, strokeInfo, editable } = this.props;
        let { cursor, pointerEvents } = this.props;
        const isCustom = model.bgShape === GraphType.CUSTOM;

        const { scaleW, scaleH, tranStr } = isCustom ? this.getScaleInfo(path, model) : ({ } as any);
        const scale = scaleW ? 1 / scaleW : 1;
        const strokeData = strokeInfo.getStrokeData(this.props.model, scale);
        const strokeWidth = imageBorderOutside ? 2 * strokeData.strokeWidth : strokeData.strokeWidth;
        const strokeOpacity = this.getStrokeOpacity(path, strokeData, isCustom);
        const fillStyle = this.getFillStyleByPath(path, isCustom);
        if (!cursor) {
            cursor = !editable ? 'auto' : 'move';
        }
        pointerEvents = pointerEvents ? pointerEvents : 'auto';
        const pathString = isCustom ? path.pathString : path.svgPath;
        const fillMode = this.getFillMode(path, isCustom);
        return {
            fillMode,
            fillStyle,
            pathString,
            scaleW,
            scaleH,
            tranStr,
            strokeData,
            strokeWidth,
            notStroke: path.notStroke,
            strokeOpacity,
            cursor,
            pointerEvents,
        };
    }

    /**
     * 是否对 image 增加滤镜
     */
    private isShowImageFilter = (image) => {
        if (!image) return false;
        const { sharpenSoften, contrast, colorTemperature } = image;
        return sharpenSoften
            || contrast
            || colorTemperature !== ImagePropsField.COLORTEMPERATURE.defaultValue;
    };

    /**
     * 渲染普通形状或自定义的形状
     * @param path
     * @param i
     */
    private renderPath = (path, i) => {
        const renderInfo = this.getPathRenderInfo(path);
        const { pathString, scaleW, scaleH } = renderInfo;

        const { model, relativeRotate = 0, effectFilter, imageFilter } = this.props;

        const { rotate = 0, width, height, image } = model;
        const rotatedDeg = rotate + relativeRotate;
        const { shadow, glow, softEdge, reflection } = model.effect || {};

        const {
            shadowScale, shadowOffsetX, shadowOffsetY, shadowFilter,
            // eslint-disable-next-line no-nested-ternary
        } = shadow
            ? shadow.isOuter
                ? effectFilter.defineOuterShadowFilter(shadow, i, width, height)
                : effectFilter.defineInnerShadowFilter(shadow, i, width, height)
            : ({} as any);

        const {
            reflectionRotationStyle, reflectionFilter,
        } = reflection
            ? effectFilter.defineReflectionFilter(
                reflection, i, width, height,
                shadowOffsetX, shadowOffsetY, rotatedDeg,
            )
            : ({} as any);

        const {
            softEdgeFilter,
        } = softEdge ? effectFilter.defineSoftEdgeFilter(softEdge, pathString, i, scaleW, scaleH) : ({} as any);

        const { glowFilter } = glow ? effectFilter.defineGlowFilter(glow, i, width, height) : ({} as any);

        const { imageFilters, imageFiltersId } = this.isShowImageFilter(image)
            ? imageFilter.defineImageFilter(i, image) : ({} as any);

        const effectRenderData = { i, shadowScale, shadowOffsetX, shadowOffsetY, reflectionRotationStyle };
        return (
            <g key={i}>
                { this.renderEffectDefs(shadowFilter, reflectionFilter, softEdgeFilter, glowFilter) }
                <g>
                    { this.renderWithReflectionOrNot(this.renderWithShadowOrNot(
                        this.renderWithGlowOrSoftEdgeOrNeither(this.renderWidthRegulateOrNot(
                            this.renderInnerPath(Object.assign({}, renderInfo, { imageFiltersId })),
                            { imageFilters, imageFiltersId },
                        ), effectRenderData),
                        effectRenderData,
                    ), effectRenderData) }
                </g>
            </g>
        );
    };

    private renderEffectDefs(shadowFilter, reflectionFilter, softEdgeFilter, glowFilter) {
        const { effect, sharpenSoften, contrast } = this.props.model;
        const hasEffect = !!effect;
        const { shadow, glow, softEdge, reflection } = effect || {};

        if (hasEffect || sharpenSoften || contrast) {
            return (<defs>
                {shadow && shadowFilter}
                {reflection && reflectionFilter}
                {softEdge && softEdgeFilter}
                {glow && glowFilter}
            </defs>);
        }
        return null;
    }

    /**
     * 是否渲染倒影效果
     * @param children
     * @param effectRenderData
     */
    private renderWithReflectionOrNot(children, effectRenderData) {
        const { i, reflectionRotationStyle } = effectRenderData;
        const { model, effectFilter } = this.props;
        const { reflection } = model.effect || {};
        if (reflection) {
            const effectsMarkId = effectFilter.getEffectsMarkId();
            const reflectionMark = { id: `use-reflection-${effectsMarkId}-${i}` };
            return (<React.Fragment>

                {reflection && effectFilter.setReflection(effectsMarkId, i, reflectionRotationStyle)}

                <g {...reflectionMark}>
                    { children }
                </g>
            </React.Fragment>);
        }
        return children;
    }

    /**
     * 是否渲染阴影效果
     * @param children
     * @param effectRenderData
     */
    private renderWithShadowOrNot(children, effectRenderData) {
        const { i, shadowScale, shadowOffsetX, shadowOffsetY } = effectRenderData;
        const { model, effectFilter } = this.props;
        const { shadow } = model.effect || {};
        if (shadow) {
            const effectsMarkId = effectFilter.getEffectsMarkId();
            const shadowMark = shadow.isOuter ? { id: `use-shadow-${effectsMarkId}-${i}` } : { filter: `url(#inner-shadow-filter-${effectsMarkId}-${i})` };
            return (<React.Fragment>
                {/* skewX 和 skewY 表示透视阴影，暂时忽略掉透视阴影的渲染 */}
                {shadow.isOuter && !shadow.skewX && !shadow.skewY && effectFilter
                    .setOuterShadow(effectsMarkId, i, shadowScale, shadowOffsetX, shadowOffsetY)}

                <g {...shadowMark}>
                    { children }
                </g>
            </React.Fragment>);
        }
        return children;
    }

    /**
     * 是否渲染发光和柔滑效果（应对的id需要绑在同一个元素上）
     * @param children
     * @param effectRenderData
     */
    private renderWithGlowOrSoftEdgeOrNeither(children, effectRenderData) {
        const { i } = effectRenderData;
        const { model, effectFilter } = this.props;
        const { glow, softEdge } = model.effect || {};
        if (glow || softEdge) {
            const effectsMarkId = effectFilter.getEffectsMarkId();
            const glowMark = { id: `use-glow-${effectsMarkId}-${i}` };
            const softEdgeMark = softEdge ? { mask: `url(#soft-edge-mask-${effectsMarkId}-${i})` } : null;
            return (<React.Fragment>

                {glow && effectFilter.setGlow(effectsMarkId, i)}

                <g {...glowMark} {...softEdgeMark}>
                    {children}
                </g>
            </React.Fragment>);
        }
        return children;
    }

    /**
     * 是否渲染图片校正
     * @param children
     * @param regulateRenderData
     */
    private renderWidthRegulateOrNot(children, imageRenderData) {
        const { image } = this.props.model;
        const { imageFilters } = imageRenderData;

        return (
            <React.Fragment>
                { this.isShowImageFilter(image) ? (
                    <defs>
                        { imageFilters }
                    </defs>
                ) : null }
                {children}
            </React.Fragment>
        );
    }

    private renderTransparentStroke = (
        strokeWidth, strokeData, pathString, cursor,
        pointerEvents, transformString?,
    ) => (
        <path
            stroke={'transparent'}
            strokeWidth={strokeWidth + (10 / this.getScaleNum())}
            strokeDasharray={strokeData.dashArray}
            strokeLinejoin={strokeData.lineJoin}
            strokeLinecap={strokeData.lineCap}
            strokeOpacity={1}
            fill={'none'}
            d={pathString}
            className={Cursor[cursor]}
            transform={transformString}
            pointerEvents={pointerEvents}
        />);

    /**
     * 真正去渲染path，包括背景，边框等等
     * @param renderInfo
     */
    private renderInnerPath(renderInfo) {
        const {
            fillMode,
            fillStyle,
            pathString,
            scaleW,
            scaleH,
            strokeData,
            tranStr,
            cursor,
            pointerEvents,
            strokeWidth,
            strokeOpacity,
            imageFiltersId,
            notStroke,
        } = renderInfo;

        const { imageBorderOutside, editable } = this.props;
        return (<React.Fragment>
            {
                !imageBorderOutside
                && this.renderBg(
                    fillMode, pathString,
                    pointerEvents, cursor, tranStr, scaleW, scaleH, imageFiltersId,
                )
            }
            <path
                className={`${FILL_AND_STROKE} ${Cursor[cursor]}`}
                stroke={notStroke ? null : strokeData.strokeColor}
                strokeWidth={notStroke ? null : strokeWidth}
                strokeDasharray={notStroke ? null : strokeData.dashArray}
                strokeLinejoin={notStroke ? null : strokeData.lineJoin}
                strokeLinecap={notStroke ? null : strokeData.lineCap}
                strokeOpacity={strokeOpacity}
                fill={fillStyle}
                fillOpacity={this.getFillOpacity()}
                fillRule={'evenodd'}
                d={pathString}
                transform={tranStr}
                pointerEvents={pointerEvents}

                // 让firefox该区域可以选择editor文字。
                onMouseDown={e => e.preventDefault()}
                style={{
                    userSelect: 'text', MozUserSelect: 'text', WebkitUserSelect: 'text',
                }}
            />
            {
                editable
                && this.renderTransparentStroke(
                    strokeWidth, strokeData,
                    pathString, cursor, pointerEvents, tranStr,
                )
            }
            {
                imageBorderOutside
                && this.renderBg(
                    fillMode, pathString,
                    pointerEvents, cursor, tranStr, scaleW, scaleH, imageFiltersId,
                )
            }
        </React.Fragment>);
    }
}

export default connect(
    GraphicContentPresenter,
    GraphicContentModel,
    GraphicContent,
);

export const testObj = {
    GraphicContent,
};
